import { useEffect, useState } from "react";
import { SessionsTable } from "../../components/history/SessionsTable";
import { StatRow } from "../../components/history/StatRow";
import { FocusCard } from "../../components/history/FocusCard";
import { TrendStrip } from "../../components/welcome/TrendStrip";
import { authedFetch } from "../../lib/api-client";
import type { ApiEnvelope } from "@a2/core/schemas";
import type { AdminSessionRow } from "../../lib/admin/types";
import { useT } from "../../lib/use-t";

type MySessionsResponse = {
  sessions: Array<{
    id: string;
    mode: string;
    startedAt: number;
    finishedAt: number | null;
    totalScore: number | null;
    maxScore: number | null;
    percentage: number | null;
    passed: boolean | null;
  }>;
  page: number;
  pageSize: number;
  total: number;
};

function hydrateSessions(payload: MySessionsResponse): AdminSessionRow[] {
  return payload.sessions.map((s) => ({
    id: s.id,
    mode: s.mode,
    startedAt: new Date(s.startedAt * 1000).toISOString(),
    finishedAt: s.finishedAt ? new Date(s.finishedAt * 1000).toISOString() : null,
    totalScore: s.totalScore,
    maxScore: s.maxScore,
    percentage: s.percentage,
    passed: s.passed,
  }));
}

export function GeschiedenisPage() {
  const t = useT();
  const [sessions, setSessions] = useState<AdminSessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch("/sessions/mine");
        if (!res.ok) {
          console.error(`[voortgang] sessions/mine HTTP ${res.status}`);
          if (!cancelled) setError(t("history.panel.loadError"));
          return;
        }
        const json = (await res.json()) as ApiEnvelope<MySessionsResponse>;
        if (cancelled) return;
        if (!json.ok) {
          console.error("[voortgang] sessions/mine envelope error", json.error);
          setError(json.error.message);
          return;
        }
        setSessions(hydrateSessions(json.data));
      } catch (err) {
        console.error("[voortgang] sessions/mine fetch failed", err);
        if (!cancelled) setError(t("history.panel.loadError"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const total = sessions?.length ?? 0;

  return (
    <main className="flex-1 mx-auto w-full max-w-6xl px-6 sm:px-8 py-8 sm:py-10 pb-24 lg:pb-10 space-y-6 lg:space-y-8">
      <header className="space-y-1.5">
        <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {t("history.voortgang.eyebrow")}
        </p>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight">
          {t("history.voortgang.title")}
        </h1>
        <p className="text-sm sm:text-base text-[var(--ink-muted)] max-w-2xl">
          {t("history.voortgang.subtitle")}
        </p>
      </header>

      {error ? (
        <p role="alert" className="text-sm text-[var(--fail)]">
          {error}
        </p>
      ) : null}

      <StatRow sessions={error ? [] : sessions} />

      <div className="grid gap-5 lg:grid-cols-[1.7fr_1fr] items-start">
        <section className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-5 sm:p-6 shadow-[var(--shadow)]">
          <h2 className="text-lg font-semibold tracking-tight mb-4">
            {t("history.voortgang.chartTitle")}
          </h2>
          <TrendStrip />
        </section>
        <FocusCard />
      </div>

      <section aria-labelledby="sessions-heading" className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="sessions-heading" className="text-lg font-semibold tracking-tight">
            {t("history.voortgang.sessionsHeading")}
          </h2>
          {sessions !== null && total > 0 ? (
            <span className="font-mono-badge text-xs text-[var(--ink-faint)]">
              {t("history.voortgang.sessionsCountFmt", { count: total })}
            </span>
          ) : null}
        </div>
        {sessions === null ? (
          <div className="h-32 animate-pulse rounded-[var(--radius-lg)] bg-[var(--panel-soft)]" aria-hidden />
        ) : (
          <SessionsTable
            sessions={sessions}
            linkContext="history"
            emptyCopy={t("history.panel.sessionsEmptyCopy")}
          />
        )}
      </section>
    </main>
  );
}
